import api from './api.service';
import type { ApiResponse } from '@/types';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type CrmFollowUpStatus = 'Open' | 'Done' | 'Rescheduled' | 'Cancelled';
export type CrmFollowUpBucket = 'overdue' | 'today' | 'this_week' | 'upcoming' | 'done';
export type CrmActivitySentiment = 'Positive' | 'Neutral' | 'Negative';
export type CrmCommittedTo = 'customer' | 'internal';

export interface CrmUserRef {
  id: string;
  first_name: string;
  last_name?: string;
  email?: string;
}

export interface CrmActivityType {
  id: string;
  company_id: string;
  code: string;
  name: string;
  counts_as_customer_contact: boolean;
  requires_outcome: boolean;
  is_system: boolean;
  sort_order: number;
  is_active: boolean;
}

export interface CrmActivity {
  id: string;
  company_id: string;
  activity_type_id: string | null;
  activity_type_code: string;
  activity_type: string;
  activity_type_name: string;
  counts_as_customer_contact: boolean;
  activity_date: string;
  duration_minutes: number | null;
  lead_id: string | null;
  opportunity_id: string | null;
  customer_id: string | null;
  demo_id: string | null;
  contact_name: string | null;
  subject: string | null;
  description: string | null;
  customer_requirements: string | null;
  outcome: string | null;
  sentiment: CrmActivitySentiment | null;
  is_stage_change: boolean;
  old_stage: string | null;
  new_stage: string | null;
  is_commitment: boolean;
  commitment_text: string | null;
  committed_to: CrmCommittedTo | null;
  follow_up_date: string | null;
  follow_up_time?: string | null;
  follow_up_owner: string | null;
  follow_up_status: CrmFollowUpStatus | null;
  completed_at: string | null;
  completed_by: string | null;
  completion_note: string | null;
  parent_activity_id: string | null;
  performed_by: string;
  sales_team_id: string | null;
  is_overdue: boolean;
  created_at: string;
  performer?: CrmUserRef;
  follow_up_owner_user?: CrmUserRef | null;
  lead?: { id: string; lead_number: string; customer_name: string } | null;
  opportunity?: { id: string; opportunity_number: string; opportunity_name: string; customer_name: string } | null;
  customer?: { id: string; customer_code: string; customer_name: string } | null;
}

export interface CrmActivityInput {
  activity_type_code: string;
  activity_date?: string;
  duration_minutes?: number | null;
  lead_id?: string | null;
  opportunity_id?: string | null;
  customer_id?: string | null;
  demo_id?: string | null;
  contact_name?: string | null;
  subject?: string | null;
  description?: string | null;
  customer_requirements?: string | null;
  outcome?: string | null;
  sentiment?: CrmActivitySentiment | null;
  is_commitment?: boolean;
  commitment_text?: string | null;
  committed_to?: CrmCommittedTo | null;
  follow_up_date?: string | null;
  follow_up_time?: string | null;
  follow_up_owner?: string | null;
  parent_activity_id?: string | null;
}

export interface CrmActivityListFilters {
  performed_by?: string;
  follow_up_owner?: string;
  sales_team_id?: string;
  date_from?: string;
  date_to?: string;
  activity_type_codes?: string;
  lead_id?: string;
  opportunity_id?: string;
  customer_id?: string;
  demo_id?: string;
  follow_up_status?: string;
  follow_up_bucket?: CrmFollowUpBucket;
  has_follow_up?: boolean;
  commitments_only?: boolean;
  include_system?: boolean;
  search?: string;
  today?: string;
  page?: number;
  limit?: number;
}

export interface CrmActivityListResponse {
  items: CrmActivity[];
  total: number;
  page: number;
  limit: number;
}

export interface CrmActivityUserSummary {
  user_id: string;
  user_name: string;
  total: number;
  customer_contacts: number;
  follow_ups_set: number;
  by_type: Record<string, number>;
}

export type CrmFollowUpSummary = Record<CrmFollowUpBucket, number> & { commitments_open: number };

function toParams(filters: object): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = String(v);
  }
  return out;
}

class CrmActivityService {
  async list(filters: CrmActivityListFilters): Promise<CrmActivityListResponse> {
    const res = await api.get('/crm/activities', { params: toParams(filters) });
    return unwrap<CrmActivityListResponse>(res) ?? { items: [], total: 0, page: 1, limit: 50 };
  }

  async summary(filters: CrmActivityListFilters): Promise<CrmActivityUserSummary[]> {
    const res = await api.get('/crm/activities/summary', { params: toParams(filters) });
    const data = unwrap<CrmActivityUserSummary[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async followUpSummary(filters: CrmActivityListFilters): Promise<CrmFollowUpSummary> {
    const res = await api.get('/crm/activities/follow-up-summary', { params: toParams(filters) });
    return (
      unwrap<CrmFollowUpSummary>(res) ?? { overdue: 0, today: 0, this_week: 0, upcoming: 0, done: 0, commitments_open: 0 }
    );
  }

  async getById(id: string): Promise<CrmActivity> {
    const res = await api.get(`/crm/activities/${id}`);
    const data = unwrap<CrmActivity>(res);
    if (!data) throw new Error('Activity not found');
    return data;
  }

  async getChain(id: string): Promise<CrmActivity[]> {
    const res = await api.get(`/crm/activities/${id}/chain`);
    const data = unwrap<CrmActivity[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async create(body: CrmActivityInput): Promise<CrmActivity> {
    const res = await api.post('/crm/activities', body);
    const data = unwrap<CrmActivity>(res);
    if (!data) throw new Error('Failed to log activity');
    return data;
  }

  async update(id: string, body: Partial<CrmActivityInput>): Promise<CrmActivity> {
    const res = await api.put(`/crm/activities/${id}`, body);
    const data = unwrap<CrmActivity>(res);
    if (!data) throw new Error('Failed to update activity');
    return data;
  }

  async remove(id: string): Promise<void> {
    await api.delete(`/crm/activities/${id}`);
  }

  async complete(
    id: string,
    body: { completion_note?: string; status?: 'Done' | 'Cancelled'; outcome_activity?: CrmActivityInput | null }
  ): Promise<CrmActivity> {
    const res = await api.post(`/crm/activities/${id}/complete`, body);
    return unwrap<CrmActivity>(res) as CrmActivity;
  }

  async reschedule(
    id: string,
    body: { follow_up_date: string; follow_up_time?: string | null; reason?: string }
  ): Promise<CrmActivity> {
    const res = await api.post(`/crm/activities/${id}/reschedule`, body);
    return unwrap<CrmActivity>(res) as CrmActivity;
  }

  async reassign(id: string, body: { follow_up_owner: string }): Promise<CrmActivity> {
    const res = await api.post(`/crm/activities/${id}/reassign`, body);
    return unwrap<CrmActivity>(res) as CrmActivity;
  }

  async listTypes(opts?: { include_inactive?: boolean; include_system?: boolean }): Promise<CrmActivityType[]> {
    const res = await api.get('/crm/activity-types', { params: toParams(opts ?? {}) });
    const data = unwrap<CrmActivityType[]>(res);
    return Array.isArray(data) ? data : [];
  }

  async createType(body: Partial<CrmActivityType>): Promise<CrmActivityType> {
    const res = await api.post('/crm/activity-types', body);
    return unwrap<CrmActivityType>(res) as CrmActivityType;
  }

  async updateType(id: string, body: Partial<CrmActivityType>): Promise<CrmActivityType> {
    const res = await api.put(`/crm/activity-types/${id}`, body);
    return unwrap<CrmActivityType>(res) as CrmActivityType;
  }

  async getSettings(): Promise<CrmSettings> {
    const res = await api.get('/crm/settings');
    return unwrap<CrmSettings>(res) as CrmSettings;
  }

  async saveSettings(body: Partial<Pick<CrmSettings, 'stale_deal_days' | 'weekly_performance_email'>>): Promise<CrmSettings> {
    const res = await api.put('/crm/settings', body);
    return unwrap<CrmSettings>(res) as CrmSettings;
  }
}

export interface CrmSettings {
  stale_deal_days: number;
  default_stale_deal_days: number;
  weekly_performance_email: boolean;
}

export const crmActivityService = new CrmActivityService();
export default crmActivityService;
